import React, { Dispatch, SetStateAction, useState } from 'react';
import { useIsMutating } from 'react-query';
import { ErrorState } from '../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { Text } from '../../CommonComponents/OPL/Text/Text';
import { Card } from '../../CommonComponents/OPL/Card/Card';
import { useAutoAccountWorkflowResult } from './useAutoAccountWorkflowResult';
import { SummaryTable } from './components/SummaryTable/SummaryTable';
import { SegmentedControl } from '../../CommonComponents/OPL/SegmentedControl/SegmentedControl';
import { ChartsContainer } from './components/ChartsContainer/ChartsContainer';
import {
  AutoAccountWorkflowResultLoading,
  AutoAccountWorkflowResultFetchingTabs,
  AutoAccountWorkflowResultFetchingCard,
} from './AutoAccountWorkflowResult.loading';
import { Button } from '../../CommonComponents/OPL/Button/Button';
import { Lozenge } from '../../CommonComponents/OPL/Lozenge/Lozenge';
import { Content, StickyFooter } from '../../CommonComponents/OPL/StickyFooter/StickyFooter';
import { toast } from '../../CommonComponents/OPL/Toast/Toast';
import { useDocumentTitle } from '../../_hooks/useDocumentTitle';
import { MESSAGES } from '../../CommonConstants';
import { useResetModuleWrapperStyles } from '../../_hooks/useResetModuleWrapperStyles';
import { PortfolioRiskNameDTO } from './api/useGetWorkflowResultsQuery';
import { useSetSelectedPortfolioMutation } from './api/useSetSelectedPortfolioMutation';
import { useRouteParams } from './hooks/useRouteParams';
import { WhatIfs } from './components/WhatIfs/WhatIfs';
import { WHAT_IFS_MK } from './components/WhatIfs/api/useWhatIfsMutation';
import { Box } from '../../CommonComponents/OPL/Box/Box';

interface Props {
  restartWizard: Dispatch<SetStateAction<void>>;
}

export const AutoAccountWorkflowResult: React.FC<Props> = ({ restartWizard }) => {
  const { clientId } = useRouteParams();

  const { data, isLoading, actions, error, refetch } = useAutoAccountWorkflowResult();

  const {
    mutateAsync: setSelectedPortfolio,
    isLoading: isSelectingPortfolio,
  } = useSetSelectedPortfolioMutation();

  const [showWhatIfs, setShowWhatIfs] = useState(false);

  const isUpdatingResult = useIsMutating({ mutationKey: WHAT_IFS_MK }) > 0;

  useDocumentTitle('Automated Account - Results');

  useResetModuleWrapperStyles();

  if (isLoading) {
    return <AutoAccountWorkflowResultLoading />;
  }

  if (error) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
        <ErrorState onAction={refetch} />
      </Box>
    );
  }

  if (!data) {
    return null;
  }

  const { tabIndex } = data;

  if (tabIndex === null) {
    return null;
  }

  const { tabNameList, tabContent } = data;
  const { setTabIndex } = actions;
  const {
    portfolioName,
    estimatedValue,
    riskScore,
    assetAllocation,
    name,
    estimatedValueYear,
    portfolioId,
    estimatedPerformance,
  } = tabContent;

  const handleOpenAccount = async () => {
    try {
      await setSelectedPortfolio({ portfolioId });

      ADV.util.Router.go2('AutoAccountAgreements', { clientId });
    } catch (err) {
      console.error(err);

      toast.error(MESSAGES.GENERIC_ERROR);
    }
  };

  return (
    <Box minWidth={864}>
      <Content>
        <Box display="flex" justifyContent="center" mt={96}>
          <Box>
            {isUpdatingResult ? (
              <AutoAccountWorkflowResultFetchingTabs />
            ) : (
              <Box width="550px" m="auto">
                <Text fontWeight={500} fontSize="h6" color="neutral.500" mb="32" textAlign="center">
                  Based on your answers here’s our recommended portfolio:
                </Text>

                <SegmentedControl
                  id="PortfolioRisk"
                  values={tabNameList}
                  selectedIndex={tabIndex}
                  onChange={setTabIndex}
                  ariaLabel="Select Portfolio risk"
                />
              </Box>
            )}

            <Box display="flex">
              {isUpdatingResult ? (
                <AutoAccountWorkflowResultFetchingCard />
              ) : (
                <Card mt="32" p="32" width="550px" display="flex" flexDirection="column">
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                    pb="16"
                  >
                    <Text variant="cardTitle">{portfolioName}</Text>

                    {name === PortfolioRiskNameDTO.Recommended && (
                      <Lozenge variantColor="success" isAlt={false} size="small" ml="16">
                        {name}
                      </Lozenge>
                    )}
                  </Box>

                  <SummaryTable
                    estimatedValue={estimatedValue}
                    riskScore={riskScore}
                    estimatedValueYear={estimatedValueYear}
                  />

                  <ChartsContainer assetAllocation={assetAllocation} />

                  <Box display="flex" justifyContent="flex-end" mt="24">
                    <Button
                      variant="subtleActive"
                      onClick={() => setShowWhatIfs(!showWhatIfs)}
                      aria-expanded={showWhatIfs}
                      aria-controls="what-ifs-card"
                    >
                      What-Ifs
                    </Button>
                  </Box>
                </Card>
              )}

              {showWhatIfs && (
                <WhatIfs
                  series={estimatedPerformance.series}
                  categories={estimatedPerformance.categories}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Content>

      <StickyFooter>
        <Button
          variant="subtleInactive"
          size="large"
          onClick={() => {
            restartWizard();
          }}
        >
          Cancel
        </Button>
        <Box display="flex" justifyContent="flex-end">
          <Button
            variant="secondary"
            size="large"
            onClick={() => {
              restartWizard();
            }}
          >
            Edit my Answers
          </Button>

          <Button
            variant="primary"
            size="large"
            onClick={handleOpenAccount}
            ml="16"
            isLoading={isSelectingPortfolio}
          >
            Open Account with This Portfolio
          </Button>
        </Box>
      </StickyFooter>
    </Box>
  );
};
